/**
 * Session state: who is signed in, which club, and what currency to render in.
 *
 * Loaded once from /v1/auth/me after login and held in context. Role checks
 * here are for HIDING things the user cannot do — the API enforces the same
 * rules, and a hidden button is a courtesy, never a security boundary.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, getToken, setToken, onUnauthorized, armUnauthorized, ApiError } from './api.ts';
import { DEFAULT_CURRENCY, type CurrencyInfo } from './format.ts';
import { clearActingAs, getActingAs } from './operator.tsx';

export const ROLES = [
  'member',
  'staff',
  'manager',
  'controller',
  'club_admin',
  'betterclub_support',
] as const;
export type Role = (typeof ROLES)[number];

export interface Club {
  id: string;
  slug: string;
  brand_name: string;
  legal_name: string;
  base_currency: string;
  country_code: string;
  default_locale: string;
  prices_include_tax: boolean;
  timezone: string;
}

interface Me {
  principal: {
    role: Role;
    person_id: string | null;
    member_account_ids: string[];
    /**
     * 'all' is the office. 'listed' is a department head (0132).
     *
     * Optional because a token minted before 0132 carries no claim and
     * the API reads that as 'all' — the same default, said in the same
     * direction, so a browser holding yesterday's session sees the app
     * it saw yesterday.
     */
    department_scope?: 'all' | 'listed';
    /** Which amenities they administer. Meaningless when scope is 'all'. */
    department_modules?: string[];
  };
  club: Club;
  currency: CurrencyInfo | null;
}

/**
 * The words this club uses for things that differ by country.
 *
 * Migration 0041 built the `term` table and `fn_term`, and
 * `/v1/vocabulary` has served it since — and not one screen in this
 * application has ever called it. Every screen hardcoded American
 * English instead, which is the outcome 0041 was written to prevent, and
 * some of them hardcoded British ("Cheque number" on the pro shop till),
 * which is the outcome it was written to prevent in the other direction.
 *
 * Loaded once beside /v1/auth/me. It is thirty rows, it changes when a
 * club changes country, and a screen that has to fetch its own labels is
 * a screen that renders the key for a frame.
 */
export interface Vocabulary {
  locale: string;
  terms: Record<string, { singular: string; plural: string }>;
}

interface SessionValue {
  loading: boolean;
  me: Me | null;
  /**
   * The club's word for something. `term('check')` is "Check" at
   * Rockford, "Cheque" in Ontario, and "Check" before the vocabulary has
   * loaded — the key is always the American term, so the fallback is
   * correct rather than blank.
   */
  term: (key: string, plural?: boolean) => string;
  /**
   * True when the last sign-out was the token running out rather than
   * somebody pressing Sign out. The login card says so; without it the
   * user is dropped at a login screen with no explanation and reads it
   * as the software having lost their work.
   */
  expired: boolean;
  club: Club | null;
  currency: CurrencyInfo;
  role: Role | null;
  atLeast: (minimum: Role) => boolean;
  /**
   * Whether to draw an amenity's administrative section (0132).
   *
   * `atLeast('manager')` is not the question for an amenity, and using
   * it is how the golf section would disappear for the golf
   * professional. A department head IS a manager — of some departments —
   * so the sidebar has to ask about the module rather than about the
   * rung.
   *
   * A courtesy and never a boundary, like every other check in this
   * file: the API refuses regardless. What it prevents is a door that
   * opens onto a red banner, which reads as broken software rather than
   * as not-for-you.
   */
  runs: (module: string) => boolean;
  /**
   * The rung alone, ignoring departments — `holdsRung` in `auth.ts`.
   *
   * For the personnel screens, which a department head DOES open: the
   * API narrows the roster, the schedule, the timesheets and the leave
   * to the departments they hold rather than refusing them. Asking
   * `atLeast('manager')` there would hide the four screens a department
   * head most needs, on a rule that was written to keep them out of
   * Payables.
   */
  atLeastRung: (minimum: Role) => boolean;
  signIn: (email: string, password: string, clubSlug?: string) => Promise<void>;
  signOut: () => void;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState<Me | null>(null);
  const [vocabulary, setVocabulary] = useState<Vocabulary | null>(null);
  const [expired, setExpired] = useState(false);

  const load = useCallback(async () => {
    if (!getToken()) {
      setMe(null);
      setLoading(false);
      return;
    }
    try {
      setMe(await api<Me>('/v1/auth/me'));
      // Separately, and allowed to fail. A club that cannot fetch its own
      // nouns should get American English and a working screen, not a
      // spinner: every key IS the American term, so the fallback below
      // is a correct answer rather than a placeholder.
      try {
        setVocabulary(await api<Vocabulary>('/v1/vocabulary'));
      } catch {
        setVocabulary(null);
      }
    } catch (e) {
      // A 401 here means the stored token expired while the tab was closed.
      // Anything else is a real problem, but either way there is no session.
      if (!(e instanceof ApiError && e.status === 401)) console.error(e);
      setToken(null);
      setMe(null);
      setVocabulary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The token stopped being accepted while somebody was working.
   *
   * This is the whole fix for "token expired, would prefer it just
   * returned automatically to the login screen": dropping `me` is what
   * puts the login card up, because the gate renders it whenever there
   * is no session.
   *
   * The route is deliberately left alone. The login card replaces the
   * shell in place, so the address bar still holds the screen they were
   * on, and signing back in puts them exactly there — no return-to
   * parameter to build, and none to get wrong.
   */
  useEffect(
    () =>
      onUnauthorized(() => {
        // A support session is fifteen minutes and then it is over. The
        // person holding it is a BetterClub employee, not a member of
        // this club's staff, and offering them the club's login form is
        // offering a door they must not go through. Send them back to
        // the console they came from instead.
        if (getActingAs()) {
          clearActingAs();
          setMe(null);
          window.location.assign('/operator');
          return;
        }
        setMe(null);
        setExpired(true);
        setLoading(false);
      }),
    [],
  );

  const value = useMemo<SessionValue>(() => {
    const role = me?.principal.role ?? null;
    return {
      loading,
      me,
      expired,
      term(key, plural = false) {
        const found = vocabulary?.terms[key];
        if (found) return plural ? found.plural || found.singular : found.singular;
        // The key is the American term by construction (0041), so an
        // unknown key or an unloaded vocabulary degrades to a readable
        // word rather than to 'postal_code'.
        const words = key.replace(/_/g, ' ');
        return words.charAt(0).toUpperCase() + words.slice(1);
      },
      club: me?.club ?? null,
      currency: me?.currency ?? DEFAULT_CURRENCY,
      role,
      // Mirrors `auth.ts` exactly, including the inversion 0132 added:
      // a manager who holds only some departments is not the office, so
      // above `staff` this answers no. The API is the boundary and this
      // is the courtesy, and the two disagreeing is precisely how a
      // sidebar comes to offer a screen that refuses.
      atLeast: (minimum) => {
        if (role === null) return false;
        if (
          me?.principal.department_scope === 'listed'
          && ROLES.indexOf(minimum) > ROLES.indexOf('staff')
        ) {
          return false;
        }
        return ROLES.indexOf(role) >= ROLES.indexOf(minimum);
      },
      atLeastRung: (minimum) =>
        role !== null && ROLES.indexOf(role) >= ROLES.indexOf(minimum),
      runs: (module) => {
        if (role === null) return false;
        if (ROLES.indexOf(role) < ROLES.indexOf('manager')) return false;
        if (me?.principal.department_scope !== 'listed') return true;
        return (me?.principal.department_modules ?? []).includes(module);
      },
      async signIn(email, password, clubSlug) {
        const res = await api<{ token: string }>('/v1/auth/login', {
          method: 'POST',
          body: { email, password, club_slug: clubSlug },
        });
        setToken(res.token);
        // A new token is a new session: let the next expiry be announced
        // too, and stop telling somebody who has just signed in that
        // their session ended.
        armUnauthorized();
        setExpired(false);
        setLoading(true);
        await load();
      },
      signOut() {
        setToken(null);
        armUnauthorized();
        setExpired(false);
        setMe(null);
        setVocabulary(null);
      },
      refresh: load,
    };
  }, [loading, me, expired, vocabulary, load]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
